import { z } from 'zod'
import { findEndpointByRequest } from 'leviathan-schemas/contract'
import { env } from '@/config/env'
import { ApiError } from './error'
import { adaptCanonicalResponse } from '@/lib/utils/canonical-response-adapter'
import { useImpersonationStore } from '@/stores/impersonation.store'

// Zod schema for refresh token response validation
const RefreshTokenResponseSchema = z.object({
  accessToken: z.string(),
})

/**
 * Strict contract validation policy:
 *   - `MODE === 'test'` → always throw on drift (tests must be fail-fast)
 *   - `VITE_STRICT_VALIDATION === 'true'` → throw in dev when flag set
 *   - otherwise → `console.warn` so prod traffic survives a schema drift
 *
 * The goal is to surface backend↔frontend contract drift at call time instead
 * of letting it rot silently (the exact failure mode that masked the
 * `Facts`/`Dimensions` casing bug for weeks).
 */
function isStrictValidationMode(): boolean {
  const mode = import.meta.env.MODE
  if (mode === 'test') return true
  return import.meta.env.VITE_STRICT_VALIDATION === 'true'
}

/**
 * Validate a raw response against its endpoint-map `responseBody` schema.
 * Called BEFORE the canonical→legacy adapter so canonical shapes validate
 * correctly. No-op when no endpoint / schema is registered.
 */
function validateAgainstContract(
  method: string,
  endpoint: string,
  raw: unknown
): void {
  const ep = findEndpointByRequest(method, endpoint)
  if (!ep?.responseBody) return

  const result = ep.responseBody.safeParse(raw)
  if (result.success) return

  const issues = result.error.issues
    .map((i) => `${i.path.join('.') || '<root>'}: ${i.message}`)
    .join('; ')
  const msg = `[${ep.operationId}] response body violates contract: ${issues}`

  if (isStrictValidationMode()) {
    throw new ApiError(msg, 500, 'CONTRACT_VIOLATION')
  }
  console.warn('[API Validation]', msg)
}

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'

interface RequestConfig {
  method?: HttpMethod
  headers?: Record<string, string>
  body?: unknown
  params?: Record<string, string | number | boolean | undefined>
  skipAuth?: boolean
}

interface ApiResponse<T> {
  data: T
  message?: string
}

// Token storage
const TOKEN_KEY = 'leviathan_access_token'
const REFRESH_TOKEN_KEY = 'leviathan_refresh_token'

export function getAccessToken(): string | null {
  return localStorage.getItem(TOKEN_KEY)
}

export function setTokens(accessToken: string, refreshToken?: string): void {
  localStorage.setItem(TOKEN_KEY, accessToken)
  if (refreshToken) {
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken)
  }
}

export function clearTokens(): void {
  localStorage.removeItem(TOKEN_KEY)
  localStorage.removeItem(REFRESH_TOKEN_KEY)
}

export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_TOKEN_KEY)
}

// Build URL with query params
function buildUrl(
  endpoint: string,
  params?: Record<string, string | number | boolean | undefined>
): string {
  const url = new URL(endpoint, env.API_URL)

  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        url.searchParams.append(key, String(value))
      }
    })
  }

  return url.toString()
}

// Refresh token logic
let refreshPromise: Promise<string> | null = null

async function refreshAccessToken(): Promise<string> {
  const refreshToken = getRefreshToken()

  if (!refreshToken) {
    throw new ApiError('No refresh token available', 401)
  }

  const response = await fetch(buildUrl('/auth/refresh'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ refreshToken }),
  })

  if (!response.ok) {
    clearTokens()
    throw new ApiError('Session expired', 401)
  }

  const data = await response.json()

  // Validate refresh response shape (warn-only, never throw in production)
  const parsed = RefreshTokenResponseSchema.safeParse(data)
  if (!parsed.success) {
    console.warn(
      '[API Validation] /auth/refresh response mismatch:',
      parsed.error.issues.map((i) => `${i.path.join('.')}: ${i.message}`).join(', ')
    )
  }

  setTokens(data.accessToken, data.refreshToken)
  return data.accessToken
}

/**
 * Returns the active impersonation token when the impersonation store has a
 * non-expired session. Used by the request pipeline to override the normal
 * access token so downstream calls reach the target tenant.
 *
 * Guarded against stubbed zustand stores (some unit tests replace `create`
 * with a no-op that does not expose `getState`).
 */
function getActiveImpersonationToken(): string | null {
  try {
    const store = useImpersonationStore as unknown as {
      getState?: () => {
        accessToken: string | null
        expiresAt: string | null
      }
    }
    if (typeof store.getState !== 'function') return null
    const state = store.getState()
    if (
      state.accessToken === null ||
      state.expiresAt === null ||
      new Date(state.expiresAt).getTime() <= Date.now()
    ) {
      return null
    }
    return state.accessToken
  } catch {
    return null
  }
}

async function getValidToken(): Promise<string | null> {
  const impersonation = getActiveImpersonationToken()
  if (impersonation !== null) return impersonation

  const token = getAccessToken()

  if (!token) {
    return null
  }

  // Check if token is expired (simple JWT decode)
  try {
    const payload = JSON.parse(atob(token.split('.')[1]))
    const expiresAt = payload.exp * 1000
    const now = Date.now()

    // If token expires in less than 5 minutes, refresh it
    if (expiresAt - now < 5 * 60 * 1000) {
      // Deduplicate refresh requests
      if (!refreshPromise) {
        refreshPromise = refreshAccessToken().finally(() => {
          refreshPromise = null
        })
      }
      return await refreshPromise
    }

    return token
  } catch {
    return token
  }
}

// Main fetch function
async function apiFetch<T>(
  endpoint: string,
  config: RequestConfig = {}
): Promise<T> {
  const { method = 'GET', headers = {}, body, params, skipAuth = false } = config

  // Build headers
  const requestHeaders: Record<string, string> = {
    'Content-Type': 'application/json',
    ...headers,
  }

  // Add auth header
  if (!skipAuth) {
    const token = await getValidToken()
    if (token) {
      requestHeaders['Authorization'] = `Bearer ${token}`
    }
  }

  // Build request
  const requestInit: RequestInit = {
    method,
    headers: requestHeaders,
  }

  if (body && method !== 'GET') {
    requestInit.body = JSON.stringify(body)
  }

  // Make request
  const url = buildUrl(endpoint, params)
  const response = await fetch(url, requestInit)

  // Handle response
  if (!response.ok) {
    let errorData: { message?: string; code?: string } = {}

    try {
      errorData = await response.json()
    } catch {
      // Response body is not JSON
    }

    // Handle 401 - try to refresh token once
    if (response.status === 401 && !skipAuth) {
      try {
        await refreshAccessToken()
        // Retry the request with new token
        return apiFetch(endpoint, config)
      } catch {
        // Refresh failed, clear tokens and throw
        clearTokens()
        window.dispatchEvent(new CustomEvent('auth:logout'))
        throw new ApiError(
          errorData.message || 'Session expired',
          401,
          errorData.code
        )
      }
    }

    throw new ApiError(
      errorData.message || `Request failed with status ${response.status}`,
      response.status,
      errorData.code,
      errorData
    )
  }

  // Handle 204 No Content
  if (response.status === 204) {
    return undefined as T
  }

  const raw = await response.json()
  // Validate against the endpoint-map contract BEFORE the canonical adapter
  // converts analytics bodies to legacy shape — the contract describes the
  // wire format, not the adapter's frontend-internal shape.
  validateAgainstContract(method, endpoint, raw)
  // Auto-convert canonical analytics responses to legacy flat format
  const data = adaptCanonicalResponse(raw)
  return data as T
}

// Convenience methods
export const api = {
  get: <T>(endpoint: string, params?: RequestConfig['params']) =>
    apiFetch<T>(endpoint, { method: 'GET', params }),

  post: <T>(endpoint: string, body?: unknown) =>
    apiFetch<T>(endpoint, { method: 'POST', body }),

  put: <T>(endpoint: string, body?: unknown) =>
    apiFetch<T>(endpoint, { method: 'PUT', body }),

  patch: <T>(endpoint: string, body?: unknown) =>
    apiFetch<T>(endpoint, { method: 'PATCH', body }),

  delete: <T>(endpoint: string) => apiFetch<T>(endpoint, { method: 'DELETE' }),

  // For public endpoints that don't need auth
  public: {
    post: <T>(endpoint: string, body?: unknown) =>
      apiFetch<T>(endpoint, { method: 'POST', body, skipAuth: true }),
  },
}

export type { ApiResponse }
